/**
 * 🟪 P24.9-CODE (rediseño 2026-02) · Visor de archivos del proyecto abierto.
 *
 * RESPONSABILIDAD ÚNICA: mostrar los archivos del proyecto que está
 * abierto en el Workspace en este momento.
 *
 * NO depende de: chat / historial / conversations.generated_files /
 * builder interno.
 *
 * Recibe `projectFiles` como prop (array ya calculado en Dashboard.jsx
 * a partir del proyecto activo). Al montarse, autoselecciona el primer
 * archivo. No hace ninguna llamada al backend.
 *
 * FIX (feedback owner 2026-02):
 *   1. Selección por ÍNDICE (no por path) — evita colisiones cuando
 *      varios archivos comparten el mismo nombre auto-generado
 *      ("file_1.jsx"). Ese era el motivo por el que el click no
 *      "hacía nada" visualmente.
 *   2. Nombre real derivado del código con esta prioridad:
 *        1º) `export default function XxxComp` / `function XxxComp(`
 *            / `const XxxComp = ` → `XxxComp.jsx`
 *        2º) basename del filepath original.
 *        3º) Fallback → `App.jsx` (con sufijo -1, -2… si colisionan).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Download, X, FileCode2, Package, FolderOpen, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import JSZip from 'jszip';

// ────────────────────────────────────────────────────────────
// Detección de lenguaje por extensión.
// ────────────────────────────────────────────────────────────
function detectLanguage(filename = '', explicitLang = '') {
  if (explicitLang) return explicitLang.toLowerCase();
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const map = {
    jsx: 'jsx', tsx: 'tsx',
    js: 'javascript', mjs: 'javascript', cjs: 'javascript',
    ts: 'typescript',
    py: 'python',
    css: 'css', scss: 'scss', sass: 'sass',
    json: 'json',
    md: 'markdown',
    html: 'html', htm: 'html',
    yaml: 'yaml', yml: 'yaml',
    sh: 'bash', bash: 'bash',
    sql: 'sql',
    go: 'go', rs: 'rust', rb: 'ruby', java: 'java', kt: 'kotlin', swift: 'swift',
    xml: 'xml',
    dockerfile: 'docker',
  };
  return map[ext] || 'text';
}

// ────────────────────────────────────────────────────────────
// Derivación de nombre real del archivo a partir del código.
// ────────────────────────────────────────────────────────────
const AUTO_GENERATED_NAME = /^file[_-]?\d+\.(jsx?|tsx?|js|ts)$/i;

function deriveComponentName(code = '') {
  if (!code) return null;
  // Buscar declaraciones típicas de componentes React.
  const patterns = [
    /export\s+default\s+function\s+([A-Z][A-Za-z0-9_]{1,60})/,
    /export\s+(?:const|function)\s+([A-Z][A-Za-z0-9_]{1,60})/,
    /function\s+([A-Z][A-Za-z0-9_]{1,60})\s*\(/,
    /const\s+([A-Z][A-Za-z0-9_]{1,60})\s*=\s*(?:\(|function|React\.memo)/,
    /class\s+([A-Z][A-Za-z0-9_]{1,60})\s+extends/,
  ];
  for (const re of patterns) {
    const m = code.match(re);
    if (m && m[1]) return m[1];
  }
  return null;
}

function extensionFor(language = '', originalPath = '') {
  const pathExt = (originalPath.split('.').pop() || '').toLowerCase();
  if (pathExt && pathExt.length <= 5) return pathExt;
  const langMap = {
    javascript: 'js', typescript: 'ts',
    jsx: 'jsx', tsx: 'tsx',
    python: 'py', css: 'css', html: 'html', json: 'json', md: 'md',
  };
  return langMap[(language || '').toLowerCase()] || 'jsx';
}

// Normaliza un archivo bruto (varias formas) → {path, name, content, language}.
function normalizeFile(f) {
  if (!f || typeof f !== 'object') return null;
  const rawPath = f.path || f.file_path || f.filepath || f.filename || '';
  const content = f.content ?? f.code ?? f.codigo ?? '';
  const language = f.language || f.lang || '';
  const basename = rawPath ? rawPath.split('/').pop() : '';
  return { rawPath, basename, content, language };
}

// A partir de la lista bruta, calcula los nombres finales SIN duplicados
// tipográficos (Xxx-1.jsx, Xxx-2.jsx si el mismo displayName apareciera
// dos veces). NO deduplicamos por identidad conceptual — el owner quiere
// ver TODAS las versiones históricas.
function computeDisplayNames(rawFiles) {
  const used = new Set();
  return rawFiles.map((f) => {
    // 1º) Componente derivado del código.
    let base = deriveComponentName(f.content);
    let ext = extensionFor(f.language, f.rawPath);
    if (base) {
      let name = `${base}.${ext}`;
      if (!used.has(name)) {
        used.add(name);
        return { ...f, displayName: name };
      }
      let i = 1;
      while (used.has(`${base}-${i}.${ext}`)) i++;
      const n = `${base}-${i}.${ext}`;
      used.add(n);
      return { ...f, displayName: n };
    }

    // 2º) basename del filepath si NO es auto-generado tipo "file_1.jsx".
    if (f.basename && !AUTO_GENERATED_NAME.test(f.basename)) {
      let name = f.basename;
      if (!used.has(name)) {
        used.add(name);
        return { ...f, displayName: name };
      }
      const dot = name.lastIndexOf('.');
      const b = dot > 0 ? name.slice(0, dot) : name;
      const e = dot > 0 ? name.slice(dot) : '';
      let i = 1;
      while (used.has(`${b}-${i}${e}`)) i++;
      const n = `${b}-${i}${e}`;
      used.add(n);
      return { ...f, displayName: n };
    }

    // 3º) Fallback → App.jsx (con sufijo si repite).
    let fallback = `App.${ext}`;
    if (!used.has(fallback)) {
      used.add(fallback);
      return { ...f, displayName: fallback };
    }
    let i = 1;
    while (used.has(`App-${i}.${ext}`)) i++;
    const n = `App-${i}.${ext}`;
    used.add(n);
    return { ...f, displayName: n };
  });
}

// ────────────────────────────────────────────────────────────
// Componente
// ────────────────────────────────────────────────────────────
export default function CodePanel({ projectFiles = [], language = 'es', onClose, projectName }) {
  const files = useMemo(() => {
    const raws = (Array.isArray(projectFiles) ? projectFiles : [])
      .map(normalizeFile)
      .filter((f) => f && (f.rawPath || f.content));
    return computeDisplayNames(raws);
  }, [projectFiles]);

  // 🟪 FIX owner (2026-02): selección por ÍNDICE (no por path) para
  // evitar colisiones cuando varios archivos comparten el mismo
  // filename auto-generado ("file_1.jsx"). Antes, el click no hacía
  // nada porque `files.find(...)` siempre devolvía el primero.
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [zipping, setZipping] = useState(false);

  // 🟪 P26.12 · Búsqueda Ctrl+K en el código del proyecto.
  // ─ Ctrl/Cmd+K → abre overlay flotante.
  // ─ Enter → siguiente match, Shift+Enter → anterior, Escape → cerrar.
  // ─ Click en un match → auto-selecciona el archivo y scrollea a la línea.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMatchIdx, setActiveMatchIdx] = useState(0);
  const searchInputRef = useRef(null);
  const skipNextAutoScrollRef = useRef(false);
  const [pendingLineHighlight, setPendingLineHighlight] = useState(null); // { fileIdx, lineIdx }

  // Auto-reset del índice cuando cambia la lista de archivos.
  useEffect(() => {
    if (files.length === 0) {
      setSelectedIdx(0);
      return;
    }
    if (selectedIdx >= files.length) setSelectedIdx(0);
  }, [files, selectedIdx]);

  const selected = files.length > 0 ? files[Math.min(selectedIdx, files.length - 1)] : null;

  // ────────────────────────────────────────────────────────────
  // 🟪 P25.3 · Vista completa del proyecto + highlight del activo.
  // Concatenamos TODOS los archivos en una única string (separador
  // "\n\n" = 1 línea en blanco entre archivos, mínimo visual). No hay
  // headers, no hay secciones colapsables, no se altera el código.
  // Un overlay `absolute` pinta una sombra suave sobre la sección del
  // archivo señalado en el sidebar. Auto-scroll a esa sección.
  // ────────────────────────────────────────────────────────────
  const SEP_LINES = 1; // "\n\n" entre archivos = 1 línea en blanco intermedia.
  const LINE_HEIGHT_PX = 18; // fontSize 12 * lineHeight 1.5.
  const PAD_TOP_PX = 12; // padding del <pre> del SyntaxHighlighter.

  const { fullCode, lineOffsets } = useMemo(() => {
    let cursor = 0;
    const offsets = [];
    const parts = [];
    files.forEach((f) => {
      const content = f.content || '';
      const nLines = content.split('\n').length;
      offsets.push({ start: cursor, end: cursor + nLines - 1 });
      parts.push(content);
      cursor += nLines + SEP_LINES;
    });
    return { fullCode: parts.join('\n\n'), lineOffsets: offsets };
  }, [files]);

  const scrollRef = useRef(null);

  const selOffset = lineOffsets[Math.min(selectedIdx, lineOffsets.length - 1)];
  const highlightTopPx = selOffset ? PAD_TOP_PX + selOffset.start * LINE_HEIGHT_PX : 0;
  const highlightHeightPx = selOffset
    ? (selOffset.end - selOffset.start + 1) * LINE_HEIGHT_PX
    : 0;

  // Auto-scroll: centrar el bloque del archivo seleccionado en el viewport.
  useEffect(() => {
    if (!scrollRef.current || !selOffset) return;
    // 🟪 P26.12: cuando el usuario salta a un match de búsqueda, saltamos
    // este auto-scroll de archivo — el jumpToMatch hace el scroll fino.
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }
    const container = scrollRef.current;
    // requestAnimationFrame para asegurar que SyntaxHighlighter ya pintó.
    const rafId = requestAnimationFrame(() => {
      const target =
        highlightTopPx - container.clientHeight / 2 + highlightHeightPx / 2;
      container.scrollTo({
        top: Math.max(0, target),
        behavior: 'smooth',
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [selectedIdx, files.length]);

  const t = useMemo(() => ({
    title: language === 'en' ? 'Code' : 'Códigos',
    subtitle: language === 'en'
      ? (projectName ? `Files · ${projectName}` : 'Project files')
      : (projectName ? `Archivos · ${projectName}` : 'Archivos del proyecto'),
    empty: language === 'en'
      ? 'This project has no files yet.'
      : 'Este proyecto aún no tiene archivos.',
    filesLabel: language === 'en' ? 'Files' : 'Archivos',
    copy: language === 'en' ? 'Copy' : 'Copiar',
    copied: language === 'en' ? 'Copied!' : '¡Copiado!',
    download: language === 'en' ? 'Download' : 'Descargar',
    downloadAll: language === 'en' ? 'Download ZIP' : 'Descargar ZIP',
    zipReady: language === 'en' ? 'Preparing ZIP…' : 'Preparando ZIP…',
    selectAFile: language === 'en' ? 'Select a file' : 'Selecciona un archivo',
    close: language === 'en' ? 'Close' : 'Cerrar',
    search: language === 'en' ? 'Search (Ctrl+K)' : 'Buscar (Ctrl+K)',
    searchPlaceholder: language === 'en'
      ? 'Search across all files…'
      : 'Buscar en todos los archivos…',
    noResults: language === 'en' ? 'No matches' : 'Sin resultados',
    matchOf: language === 'en' ? 'of' : 'de',
    prevMatch: language === 'en' ? 'Previous (Shift+Enter)' : 'Anterior (Shift+Enter)',
    nextMatch: language === 'en' ? 'Next (Enter)' : 'Siguiente (Enter)',
  }), [language, projectName]);

  const handleCopy = useCallback(async () => {
    if (!selected?.content) return;
    try {
      await navigator.clipboard.writeText(selected.content);
    } catch (_e) {
      const ta = document.createElement('textarea');
      ta.value = selected.content;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_c) { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }, [selected]);

  const handleDownload = useCallback(() => {
    if (!selected?.content) return;
    const filename = selected.displayName || 'file.txt';
    const blob = new Blob([selected.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [selected]);

  const handleDownloadAll = useCallback(async () => {
    if (files.length === 0 || zipping) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      files.forEach((f) => {
        if (!f.displayName) return;
        zip.file(f.displayName, f.content || '');
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const zipName = (projectName || 'damyxia-project')
        .replace(/[^\w-]+/g, '-')
        .toLowerCase();
      a.download = `${zipName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setZipping(false);
    }
  }, [files, zipping, projectName]);

  // ────────────────────────────────────────────────────────────
  // 🟪 P26.12 · Búsqueda global Ctrl+K sobre TODOS los archivos.
  // ────────────────────────────────────────────────────────────
  const MAX_MATCHES = 300;

  const matches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    const results = [];
    for (let fileIdx = 0; fileIdx < files.length; fileIdx++) {
      const f = files[fileIdx];
      const content = f.content || '';
      const lines = content.split('\n');
      for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        const line = lines[lineIdx];
        const lowerLine = line.toLowerCase();
        let pos = lowerLine.indexOf(q);
        while (pos !== -1) {
          results.push({
            fileIdx,
            fileName: f.displayName,
            lineIdx,
            line,
            col: pos,
          });
          if (results.length >= MAX_MATCHES) return results;
          pos = lowerLine.indexOf(q, pos + q.length);
        }
      }
    }
    return results;
  }, [files, searchQuery]);

  // Reset índice de match activo cuando cambia la query.
  useEffect(() => {
    setActiveMatchIdx(0);
  }, [searchQuery]);

  // Salta a un match: cambia archivo y scrollea a la línea exacta.
  const jumpToMatch = useCallback((m) => {
    if (!m || !lineOffsets[m.fileIdx]) return;
    skipNextAutoScrollRef.current = true;
    setSelectedIdx(m.fileIdx);
    setPendingLineHighlight({ fileIdx: m.fileIdx, lineIdx: m.lineIdx });
    // Esperamos al próximo frame para que React re-renderice el highlight
    // de archivo antes de calcular el scroll fino a la línea.
    setTimeout(() => {
      if (!scrollRef.current) return;
      const absLine = (lineOffsets[m.fileIdx]?.start ?? 0) + m.lineIdx;
      const target =
        PAD_TOP_PX + absLine * LINE_HEIGHT_PX - scrollRef.current.clientHeight / 2;
      scrollRef.current.scrollTo({
        top: Math.max(0, target),
        behavior: 'smooth',
      });
    }, 30);
    // El highlight de línea se auto-limpia a los 1.6s.
    setTimeout(() => setPendingLineHighlight(null), 1600);
  }, [lineOffsets]);

  const goToMatchIndex = useCallback((idx) => {
    if (matches.length === 0) return;
    const next = ((idx % matches.length) + matches.length) % matches.length;
    setActiveMatchIdx(next);
    jumpToMatch(matches[next]);
  }, [matches, jumpToMatch]);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 20);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setPendingLineHighlight(null);
  }, []);

  // Listener global Ctrl/Cmd+K + Escape.
  useEffect(() => {
    const handler = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (searchOpen) {
          closeSearch();
        } else {
          openSearch();
        }
      } else if (e.key === 'Escape' && searchOpen) {
        e.preventDefault();
        closeSearch();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [searchOpen, openSearch, closeSearch]);

  // Overlay de highlight temporal para la línea del match activo.
  const pendingLineTopPx = useMemo(() => {
    if (!pendingLineHighlight) return null;
    const off = lineOffsets[pendingLineHighlight.fileIdx];
    if (!off) return null;
    const absLine = off.start + pendingLineHighlight.lineIdx;
    return PAD_TOP_PX + absLine * LINE_HEIGHT_PX;
  }, [pendingLineHighlight, lineOffsets]);

  return (
    <div
      className="relative flex flex-col h-full bg-zinc-950 text-zinc-100"
      data-testid="code-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800/70 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <FileCode2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="code-panel-title">
              {t.title}
            </div>
            <div className="text-[10px] text-zinc-500 truncate">{t.subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={openSearch}
            disabled={files.length === 0}
            className="px-2 py-1 text-xs rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid="code-panel-search-open"
            title={t.search}
          >
            <Search className="w-3 h-3" />
            <span className="hidden sm:inline">Ctrl+K</span>
          </button>
          <button
            onClick={handleCopy}
            disabled={!selected}
            className="px-2 py-1 text-xs rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid="code-panel-copy"
            title={t.copy}
          >
            <Copy className="w-3 h-3" />
            <span className="hidden sm:inline">{copied ? t.copied : t.copy}</span>
          </button>
          <button
            onClick={handleDownload}
            disabled={!selected}
            className="px-2 py-1 text-xs rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid="code-panel-download"
            title={t.download}
          >
            <Download className="w-3 h-3" />
          </button>
          <button
            onClick={handleDownloadAll}
            disabled={files.length === 0 || zipping}
            className="px-2 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid="code-panel-download-all"
            title={t.downloadAll}
          >
            <Package className="w-3 h-3" />
            <span className="hidden sm:inline">
              {zipping ? t.zipReady : t.downloadAll}
            </span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-zinc-800"
              data-testid="code-panel-close"
              title={t.close}
              aria-label={t.close}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Sidebar — lista de archivos */}
        <aside
          className="w-44 sm:w-60 border-r border-zinc-800/60 overflow-y-auto shrink-0 bg-zinc-950"
          data-testid="code-panel-sidebar"
        >
          <div className="px-3 py-2 border-b border-zinc-800/50 flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-500">
            <FolderOpen className="w-3 h-3" />
            <span>{t.filesLabel}</span>
            {files.length > 0 && (
              <span className="ml-auto text-[10px] text-zinc-600">
                {files.length}
              </span>
            )}
          </div>
          {files.length === 0 ? (
            <div className="p-3 text-xs text-zinc-500" data-testid="code-panel-empty">
              {t.empty}
            </div>
          ) : (
            <ul className="p-1" data-testid="code-panel-file-list">
              {files.map((f, idx) => {
                const isActive = idx === selectedIdx;
                return (
                  <li key={idx}>
                    <button
                      type="button"
                      onClick={() => setSelectedIdx(idx)}
                      className={`w-full text-left px-2 py-1.5 text-xs transition-colors flex items-center gap-1.5 rounded ${
                        isActive
                          ? 'bg-blue-600 text-white font-medium'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                      data-testid={`code-panel-file-${idx}`}
                      aria-current={isActive ? 'true' : undefined}
                    >
                      <span className={isActive ? 'text-white' : 'text-emerald-400'}>●</span>
                      <span className="truncate flex-1" title={f.displayName}>
                        {f.displayName}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Área del código — vista completa con highlight del activo */}
        <div className="flex-1 min-w-0 overflow-hidden flex flex-col">
          {selected ? (
            <>
              <div
                className="px-3 py-1.5 border-b border-zinc-800/60 text-[10px] text-zinc-500 font-mono truncate"
                data-testid="code-panel-selected-name"
              >
                {selected.displayName}
              </div>
              <div
                ref={scrollRef}
                className="relative flex-1 overflow-auto text-[12px] leading-relaxed"
                data-testid="code-panel-code"
              >
                {/* 🟪 Sombra suave sobre la sección del archivo activo.
                    NO altera el contenido del código — es puramente
                    visual (pointer-events-none). */}
                {selOffset && (
                  <div
                    aria-hidden="true"
                    data-testid="code-panel-active-highlight"
                    className="absolute left-0 right-0 pointer-events-none rounded-r-md border-l-4 border-blue-400/80 bg-blue-400/10 shadow-sm transition-all duration-200"
                    style={{
                      top: `${highlightTopPx}px`,
                      height: `${highlightHeightPx}px`,
                    }}
                  />
                )}
                {/* 🟪 P26.12 · Highlight temporal de la línea del match activo. */}
                {pendingLineTopPx !== null && (
                  <div
                    aria-hidden="true"
                    data-testid="code-panel-match-highlight"
                    className="absolute left-0 right-0 pointer-events-none bg-amber-300/25 border-y border-amber-300/60 animate-pulse"
                    style={{
                      top: `${pendingLineTopPx}px`,
                      height: `${LINE_HEIGHT_PX}px`,
                    }}
                  />
                )}
                <SyntaxHighlighter
                  language={detectLanguage(selected.displayName, selected.language)}
                  style={atomDark}
                  showLineNumbers
                  wrapLongLines={false}
                  customStyle={{
                    margin: 0,
                    padding: '12px',
                    background: 'transparent',
                    fontSize: '12px',
                    lineHeight: '1.5',
                  }}
                  codeTagProps={{
                    style: {
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    },
                  }}
                >
                  {fullCode}
                </SyntaxHighlighter>
              </div>
            </>
          ) : (
            <div
              className="flex-1 flex items-center justify-center text-xs text-zinc-500 p-4"
              data-testid="code-panel-no-selection"
            >
              {files.length === 0 ? t.empty : t.selectAFile}
            </div>
          )}
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────
          🟪 P26.12 · Overlay flotante de búsqueda Ctrl+K
          ──────────────────────────────────────────────────────── */}
      {searchOpen && (
        <div
          className="absolute top-14 right-4 z-40 w-[min(90vw,420px)] rounded-lg border border-zinc-700 bg-zinc-900/95 shadow-2xl backdrop-blur"
          data-testid="code-panel-search-overlay"
          role="dialog"
          aria-label={t.search}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
            <Search className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none"
              data-testid="code-panel-search-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (matches.length === 0) return;
                  if (e.shiftKey) goToMatchIndex(activeMatchIdx - 1);
                  else goToMatchIndex(activeMatchIdx + 1);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  closeSearch();
                }
              }}
            />
            <span
              className="text-[11px] text-zinc-500 shrink-0 tabular-nums"
              data-testid="code-panel-search-count"
            >
              {matches.length === 0
                ? (searchQuery.trim().length >= 2 ? t.noResults : '')
                : `${activeMatchIdx + 1} ${t.matchOf} ${matches.length}`}
            </span>
            <button
              onClick={() => goToMatchIndex(activeMatchIdx - 1)}
              disabled={matches.length === 0}
              className="p-1 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="code-panel-search-prev"
              title={t.prevMatch}
              aria-label={t.prevMatch}
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => goToMatchIndex(activeMatchIdx + 1)}
              disabled={matches.length === 0}
              className="p-1 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="code-panel-search-next"
              title={t.nextMatch}
              aria-label={t.nextMatch}
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={closeSearch}
              className="p-1 rounded hover:bg-zinc-800"
              data-testid="code-panel-search-close"
              title={t.close}
              aria-label={t.close}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {matches.length > 0 && (
            <ul
              className="max-h-64 overflow-y-auto py-1"
              data-testid="code-panel-search-results"
            >
              {matches.slice(0, 100).map((m, idx) => {
                const isActive = idx === activeMatchIdx;
                const q = searchQuery.trim();
                const before = m.line.slice(Math.max(0, m.col - 30), m.col);
                const hit = m.line.slice(m.col, m.col + q.length);
                const after = m.line.slice(m.col + q.length, m.col + q.length + 60);
                return (
                  <li key={`${m.fileIdx}-${m.lineIdx}-${m.col}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveMatchIdx(idx);
                        jumpToMatch(m);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex flex-col gap-0.5 border-l-2 transition-colors ${
                        isActive
                          ? 'bg-blue-600/20 border-blue-400'
                          : 'border-transparent hover:bg-zinc-800/70'
                      }`}
                      data-testid={`code-panel-search-result-${idx}`}
                    >
                      <span className="flex items-center gap-2 text-[10px] text-zinc-400">
                        <FileCode2 className="w-3 h-3 text-emerald-400" />
                        <span className="truncate">{m.fileName}</span>
                        <span className="text-zinc-600">·</span>
                        <span className="tabular-nums">L{m.lineIdx + 1}</span>
                      </span>
                      <span className="font-mono text-[11px] text-zinc-300 truncate">
                        <span className="text-zinc-500">{before}</span>
                        <span className="bg-amber-400/40 text-amber-100 px-0.5 rounded-sm">
                          {hit}
                        </span>
                        <span className="text-zinc-500">{after}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {matches.length > 100 && (
                <li className="px-3 py-1 text-[10px] text-zinc-500 italic">
                  {language === 'en'
                    ? `Showing first 100 of ${matches.length}`
                    : `Mostrando 100 de ${matches.length}`}
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
